import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useGlobalAPI } from '../../../../hooks/useGlobalAPI';
import { useOcelContext } from '../../../../stores/OcelContext';
import { useGlobalFilters } from '../../../../stores/GlobalFilterContext/GlobalFilterContext';
import { VARIANTS_PER_PAGE } from '../constants';
import variantSelectorApi from '../api/variantSelectorApi';

export const useVariantExplorer = (selectedObjectType, activeFile) => {
  const api = useGlobalAPI(activeFile);
  const { dispatch, filterState } = useGlobalFilters();
  const [variants, setVariants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('trace_count');
  const [sortOrder, setSortOrder] = useState('desc');
  const hasUserTouchedSelection = useRef(false);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const { ocelData, setOcelData, isFiltered, setIsFiltered } = useOcelContext();
  
  // Track if variant filtering is currently active
  const isVariantFilterActive = filterState?.variantFilter?.enabled && 
    filterState?.variantFilter?.variantActivitiesList?.length > 0;

  // Reset all variant data when activeFile changes
  useEffect(() => {
    setVariants([]);
    setSummary(null);
    setSelectedVariants([]);
    setCurrentPage(1);
    setSortKey('trace_count');
    setSortOrder('desc');
    setLoading(false);
    setHasLoaded(false);
    setError(null);
    setOcelData(null);
    setIsFiltered(false);
    hasUserTouchedSelection.current = false;
  }, [activeFile]);

  useEffect(() => {
    if (selectedObjectType) {
      console.log('🔄 VariantExplorer effect triggered:', { selectedObjectType });
      hasUserTouchedSelection.current = false;
      setOcelData(null);
      setHasLoaded(false);
      handleFetchVariants();
    }
  }, [selectedObjectType]);

  const handleFetchVariants = async (ids = null, activityFilter = null) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching variants with current global filters (excluding variant filters)');
      
      // Get current global filters excluding variant filters
      const globalFilters = { ...filterState };
      delete globalFilters.variantFilter;
      
      // Build the filtered request using the global API
      const globalFilterRequest = api.buildFilteredRequest(globalFilters);
      
      const data = ids
        ? await variantSelectorApi.filterOCELByVariants(selectedObjectType, ids, activeFile)
        : await variantSelectorApi.getFilteredVariants(selectedObjectType, activeFile, globalFilterRequest, activityFilter);
      
      setVariants(data.variants);
      setSummary(data.summary);
      
      if (activityFilter && activityFilter.enabled) {
        const matchingVariantIds = data.variants.map((v) => v.variant_id);
        setSelectedVariants(matchingVariantIds);
        console.log(`✅ Activity filter applied. Selected ${matchingVariantIds.length} matching variants from backend`);
      } else {
        setSelectedVariants(data.variants.map((v) => v.variant_id));
      }
      
      setCurrentPage(1);
      setHasLoaded(true);
      setOcelData(data);
      setIsFiltered(!!ids);
    } catch (err) {
      console.error('Failed to fetch variant data:', err);
      setError('Failed to fetch variant data');
    } finally {
      setLoading(false);
    }
  };

  // Expose refetch function to parent component
  const refetchData = useCallback((activityFilter = null) => {
    if (selectedObjectType) {
      console.log('🔄 Refetching variant data due to global filter change or activity filter');
      handleFetchVariants(null, activityFilter);
    }
  }, [selectedObjectType, activeFile, filterState]);

  const handleToggleSelect = (variantId) => {
    hasUserTouchedSelection.current = true;
    setSelectedVariants((prev) =>
      prev.includes(variantId)
        ? prev.filter((id) => id !== variantId)
        : [...prev, variantId]
    );
  };

  const toggleSelectAll = () => {
    hasUserTouchedSelection.current = true;
    if (selectedVariants.length === variants.length) {
      setSelectedVariants([]);
    } else {
      setSelectedVariants(variants.map((v) => v.variant_id));
    }
  };

  const setSorting = (key, order = 'asc') => {
    setSortKey(key);
    setSortOrder(order);
  };

  const handleSortChange = (selected) => {
    if (selected === 'Fastest') {
      setSorting('median_lead_time', 'asc');
    } else if (selected === 'Slowest') {
      setSorting('median_lead_time', 'desc');
    } else {
      setSorting('trace_count', 'desc');
    }
  };

  const handleColumnSort = (key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const handleFilter = async () => {
    // Ensure at least one variant is selected
    if (selectedVariants.length === 0) {
      alert('Please select at least one variant to filter by.');
      return;
    }

    // Get the selected variants
    const selectedVariantObjects = variants.filter(v => selectedVariants.includes(v.variant_id));
    if (selectedVariantObjects.length === 0) {
      alert('Selected variants not found.');
      return;
    }

    // Extract activities from all selected variants
    const variantActivitiesList = selectedVariantObjects.map(variant => variant.activities);

    // Set the variant filter in the global context
    dispatch({
      type: 'SET_VARIANT_FILTER',
      payload: {
        objectType: selectedObjectType,
        variantActivitiesList: variantActivitiesList,
        enabled: true
      }
    });

    console.log(`🔄 Variant filter applied: ${selectedVariantObjects.length} variants for ${selectedObjectType}`);
    selectedVariantObjects.forEach((variant, i) => {
      console.log(`   Variant ${i + 1}: ${variant.activities.join(' → ')}`);
    });
    console.log('🔄 Filter will be applied on next component refresh triggered by effect');
  };

  const handleReset = async () => {
    // Clear ONLY the variant filter from global context
    dispatch({
      type: 'SET_VARIANT_FILTER',
      payload: {
        objectType: null,
        variantActivitiesList: null,
        enabled: false
      }
    });
    
    setSelectedVariants(variants.map((v) => v.variant_id));
    hasUserTouchedSelection.current = false;
    
    console.log('🔄 Variant filter cleared and checkboxes reset - other components will refresh with unfiltered data');
  };

  const sortedVariants = useMemo(() => {
    const sorted = [...variants].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];

      if (sortKey === 'variant_id') {
        return sortOrder === 'asc'
          ? a.variant_id.localeCompare(b.variant_id)
          : b.variant_id.localeCompare(a.variant_id);
      }

      if (sortKey === 'median_lead_time') {
        aVal = a.lead_time_stats?.median ?? 0;
        bVal = b.lead_time_stats?.median ?? 0;
      }

      aVal = aVal ?? 0;
      bVal = bVal ?? 0;

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [variants, sortKey, sortOrder]);

  const indexOfLast = currentPage * VARIANTS_PER_PAGE;
  const currentVariants = sortedVariants.slice(indexOfLast - VARIANTS_PER_PAGE, indexOfLast);
  const totalPages = Math.ceil(variants.length / VARIANTS_PER_PAGE);

  const getPageGroup = () => {
    const groupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const groupEnd = Math.min(groupStart + 4, totalPages);
    return Array.from({ length: groupEnd - groupStart + 1 }, (_, i) => groupStart + i);
  };

  const handleBulkSelection = useCallback((variantIds) => {
    hasUserTouchedSelection.current = true;
    setSelectedVariants(variantIds);
  }, []);

  return {
    loading,
    isFiltered,
    isVariantFilterActive,
    error,
    hasLoaded,
    variants,
    summary,
    selectedVariants,
    currentVariants,
    totalPages,
    currentPage,
    sortKey,
    sortOrder,
    handleToggleSelect,
    toggleSelectAll,
    handleBulkSelection,
    handleSortChange,
    handleColumnSort,
    handleFilter,
    handleReset,
    setCurrentPage,
    getPageGroup,
    refetchData,
  };
};